import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import Navbar from "./Navbar";
import Footer from "./Footer";

export default function PrivacyPolicy() {
  const { lang } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const en = {
    title: "Privacy Policy - Advaltis Limited",
    lastUpdated: "Last updated: January 2025",
    sections: [
      { heading: "1. Introduction", content: "Advaltis Limited places the highest importance on protecting your personal data. This privacy policy aims to inform you transparently about the collection and use of your data, your rights, and how to exercise them. We are committed to complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018." },
      { heading: "2. Identity of the Data Controller", content: null, list: [
        "Advaltis Limited",
        "124 City Road, London, EC1V 2NX, United Kingdom",
        "Company Number: 11823905",
        "Contact: info@advaltis.com"
      ]},
      { heading: "3. Personal Data Collected", content: "We collect only the data strictly necessary for our activities:", list: [
        "Identification data: name, surname",
        "Professional data: company, role",
        "Contact data: email address, telephone number",
        "Technical data: IP address, browsing data (cookies)"
      ]},
      { heading: "4. Purposes of Processing", content: null, subsections: [
        { sub: "4.1 Managing enquiries", text: "Responding to your messages via our contact form or email. Legal basis: legitimate interest." },
        { sub: "4.2 Business development", text: "B2B prospecting and sending relevant content or offers. Legal basis: consent." },
        { sub: "4.3 Client relationship management", text: "Preparing commercial proposals and delivering consulting engagements. Legal basis: contractual or pre-contractual necessity." },
        { sub: "4.4 Website analytics", text: "Measuring audience and improving user experience. Legal basis: consent (cookies)." }
      ]},
      { heading: "5. Mandatory Nature of Data", content: "Certain data is essential in order to process your enquiries, establish a commercial relationship, or deliver our services. Without this information, we may not be able to respond effectively." },
      { heading: "6. Data Retention", content: null, list: [
        "Prospects and clients: 5 years after the last contact",
        "Cookies: 13 months maximum"
      ]},
      { heading: "7. Data Recipients", content: "Your data may be shared with Advaltis Limited internal teams and technical service providers (hosting, SaaS tools). We never sell your data." },
      { heading: "8. International Data Transfers", content: "Certain tools may involve data transfers outside the United Kingdom. In such cases, we implement appropriate safeguards in compliance with UK GDPR (standard contractual clauses, adequacy decisions, etc.)." },
      { heading: "9. Data Security", content: "We implement appropriate security measures including restricted access to data, secure hosting, and protection against unauthorised access." },
      { heading: "10. Your Rights", content: "In accordance with UK GDPR, you have the following rights:", list: [
        "Right of access",
        "Right to rectification",
        "Right to erasure",
        "Right to object",
        "Right to restriction of processing",
        "Right to data portability",
        "Right to withdraw consent"
      ]},
      { heading: "11. Exercising Your Rights", content: "You may exercise your rights at any time by contacting us at info@advaltis.com. A response will be provided within one month." },
      { heading: "12. Supervisory Authority", content: "You may lodge a complaint with the Information Commissioner's Office (ICO): https://ico.org.uk" },
      { heading: "13. Policy Updates", content: "This policy may be updated at any time to ensure ongoing compliance with applicable regulations." }
    ]
  };

  const fr = {
    title: "Politique de Confidentialit\u00e9 - Advaltis Limited",
    lastUpdated: "Derni\u00e8re mise \u00e0 jour : janvier 2025",
    sections: [
      { heading: "1. Introduction", content: "Advaltis Limited accorde une importance primordiale \u00e0 la protection de vos donn\u00e9es personnelles. La pr\u00e9sente politique de confidentialit\u00e9 a pour objectif de vous informer de mani\u00e8re transparente sur la collecte et l\u2019utilisation de vos donn\u00e9es, vos droits et les moyens de les exercer. Nous nous engageons \u00e0 respecter le R\u00e8glement G\u00e9n\u00e9ral sur la Protection des Donn\u00e9es (RGPD) ainsi que la l\u00e9gislation britannique en mati\u00e8re de protection des donn\u00e9es." },
      { heading: "2. Identit\u00e9 du responsable de traitement", content: null, list: [
        "Advaltis Limited",
        "124 City Road, London, EC1V 2NX, Royaume-Uni",
        "Num\u00e9ro d\u2019entreprise : 11823905",
        "Contact : info@advaltis.com"
      ]},
      { heading: "3. Donn\u00e9es personnelles collect\u00e9es", content: "Nous collectons uniquement les donn\u00e9es strictement n\u00e9cessaires \u00e0 nos activit\u00e9s :", list: [
        "Donn\u00e9es d\u2019identification : nom, pr\u00e9nom",
        "Donn\u00e9es professionnelles : soci\u00e9t\u00e9, fonction",
        "Donn\u00e9es de contact : adresse email, num\u00e9ro de t\u00e9l\u00e9phone",
        "Donn\u00e9es techniques : adresse IP, donn\u00e9es de navigation (cookies)"
      ]},
      { heading: "4. Finalit\u00e9s du traitement", content: null, subsections: [
        { sub: "4.1 Gestion des demandes", text: "R\u00e9pondre \u00e0 vos messages via formulaire ou email. Base l\u00e9gale : int\u00e9r\u00eat l\u00e9gitime." },
        { sub: "4.2 D\u00e9veloppement commercial", text: "Prospection B2B et envoi de contenus ou offres. Base l\u00e9gale : consentement." },
        { sub: "4.3 Gestion des relations clients", text: "\u00c9laboration de propositions commerciales et r\u00e9alisation de missions de conseil. Base l\u00e9gale : contractuelle / pr\u00e9contractuelle." },
        { sub: "4.4 Analyse du site", text: "Mesure d\u2019audience et am\u00e9lioration de l\u2019exp\u00e9rience utilisateur. Base l\u00e9gale : consentement (cookies)." }
      ]},
      { heading: "5. Caract\u00e8re obligatoire des donn\u00e9es", content: "Certaines donn\u00e9es sont indispensables pour traiter vos demandes, \u00e9tablir une relation commerciale ou ex\u00e9cuter nos prestations. Sans ces informations, nous ne pourrons pas r\u00e9pondre efficacement." },
      { heading: "6. Dur\u00e9e de conservation", content: null, list: [
        "Prospects / clients : 5 ans apr\u00e8s le dernier contact",
        "Cookies : 13 mois maximum"
      ]},
      { heading: "7. Destinataires des donn\u00e9es", content: "Vos donn\u00e9es peuvent \u00eatre partag\u00e9es avec les \u00e9quipes internes d\u2019Advaltis Limited et les prestataires techniques (h\u00e9bergement, outils SaaS). Nous ne vendons jamais vos donn\u00e9es." },
      { heading: "8. Transferts internationaux de donn\u00e9es", content: "Certains outils peuvent entra\u00eener des transferts de donn\u00e9es hors du Royaume-Uni. Dans ce cas, nous mettons en place des garanties conformes au RGPD (clauses contractuelles types, d\u00e9cisions d\u2019ad\u00e9quation, etc.)." },
      { heading: "9. S\u00e9curit\u00e9 des donn\u00e9es", content: "Nous appliquons des mesures de s\u00e9curit\u00e9 adapt\u00e9es : acc\u00e8s restreint aux donn\u00e9es, h\u00e9bergement s\u00e9curis\u00e9 et protection contre les acc\u00e8s non autoris\u00e9s." },
      { heading: "10. Vos droits", content: "Conform\u00e9ment au RGPD, vous disposez des droits suivants :", list: [
        "Droit d\u2019acc\u00e8s",
        "Droit de rectification",
        "Droit d\u2019effacement",
        "Droit d\u2019opposition",
        "Droit \u00e0 la limitation",
        "Droit \u00e0 la portabilit\u00e9",
        "Droit de retirer votre consentement"
      ]},
      { heading: "11. Exercice de vos droits", content: "Vous pouvez exercer vos droits \u00e0 tout moment en nous contactant \u00e0 info@advaltis.com. Une r\u00e9ponse vous sera apport\u00e9e sous 1 mois maximum." },
      { heading: "12. Autorit\u00e9 de contr\u00f4le", content: "Vous pouvez introduire une r\u00e9clamation aupr\u00e8s de l\u2019Information Commissioner\u2019s Office (ICO) : https://ico.org.uk" },
      { heading: "13. Mise \u00e0 jour de la politique", content: "Cette politique peut \u00eatre modifi\u00e9e \u00e0 tout moment afin de garantir sa conformit\u00e9 avec la r\u00e9glementation en vigueur." }
    ]
  };

  const t = lang === "fr" ? fr : en;

  return (
    <>
      <Navbar />
      <main className="bg-[#0F1A2E] min-h-screen">
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-20">
          <Link to="/" className="text-[#D4AF37] text-sm hover:underline" data-testid="back-home">&larr; {lang === "fr" ? "Retour \u00e0 l\u2019accueil" : "Back to Home"}</Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white mt-6 mb-2" data-testid="privacy-title">{t.title}</h1>
          <p className="text-slate-400 text-sm mb-12">{t.lastUpdated}</p>

          {t.sections.map((s, i) => (
            <section key={i} className="mb-10">
              <h2 className="text-lg font-semibold text-white mb-3">{s.heading}</h2>
              {s.content && <p className="text-slate-300 leading-relaxed mb-3">{s.content}</p>}
              {s.list && (
                <ul className="list-disc list-inside space-y-1 text-slate-300">
                  {s.list.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              )}
              {s.subsections && s.subsections.map((sub, k) => (
                <div key={k} className="mb-3">
                  <h3 className="text-white font-medium">{sub.sub}</h3>
                  <p className="text-slate-300">{sub.text}</p>
                </div>
              ))}
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}
